<script>
  import { createEventDispatcher } from "svelte";
  import mime from "mime";
  import { loadImage } from "iso-canvas";

  import Dropzone from "#lib/dropzone/Dropzone.svelte";
  import { Modal } from "svelte-utilities";
  import ErrorMessage from "#lib/standard-inputs/ErrorMessage.svelte";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import UploadIcon from "@local/assets/icons/upload.svg";
  import XIcon from "@local/assets/icons/x.svg";
  import { downloadBlob } from "downloaders";
  import { api, storeDrawing, drawingSrc } from "#src/api";
  import sanitizePgString from "#src/extensions/sanitize-postgrest-string";
  import fileExtension from "@local/extensions/utilities/extension";

  export let drawing;
  export let recordName = "item";

  const dispatch = createEventDispatcher();

  let errorMsg = null;

  $: src = getImageThumbnail(drawing);

  let dropzoneModal;
  let dropzone;
  let removeModal;

  function getImageThumbnail(drawing) {
    if (!drawing) return "";
    return drawingSrc(drawing, { width: 256, height: 256 });
  }

  function upload() {
    dropzoneModal.open();
  }

  async function downloadDrawing() {
    try {
      const file = drawing.object_id;
      const { data, error } = await api.storage.from("drawings").download(file);
      if (error) throw error;

      downloadBlob(data, drawing.name);
    } catch (error) {
      console.log(error);
    }
  }

  function remove() {
    removeModal.open();
  }

  async function removeDrawing() {
    dispatch("delete");
  }

  async function uploadFiles(evt) {
    const { file, data } = evt.detail;
    const extension = fileExtension(file.name);
    const name = sanitizePgString(file.name);

    const id = crypto.randomUUID();
    const original = `${id}/doc.${extension}`;
    const contentType = mime.getType(extension);

    try {
      const {
        path,
        sidecarPath,
        sidecarProperties: sp,
        error,
      } = await storeDrawing(original, contentType, data);

      if (error) throw error;

      let result;
      if (sp) {
        result = {
          id,
          type: "image",
          preview_object_id: sidecarPath,
          object_id: path,
          name,
          properties: sp,
          extension,
        };
      } else {
        const blob = new Blob([data], { type: contentType });
        const url = URL.createObjectURL(blob);
        const img = await loadImage(url);
        const properties = {
          width: img.width,
          height: img.height,
        };

        result = {
          id,
          type: "image",
          preview_object_id: path,
          object_id: path,
          name,
          properties,
          extension,
        };
      }

      dispatch("upload", {
        drawing: result,
      });

      dropzone.stopLoading();
      dropzoneModal.close();
    } catch (e) {
      errorMsg = "There was an error uploading this drawing.";
      console.log(e);
      dropzone.stopLoading();
    }
  }
</script>

<div class="px-6 text-xs">
  <h3 class="mb-2">Upload Drawing</h3>
  {#if drawing}
    <div class="space-y-1">
      <div class="w-full border">
        <img class="object-contain w-full" {src} alt="Drawing Thumbnail" />
      </div>
      <div class="flex items-center justify-between my-2">
        <button class="flex items-center cursor-pointer gap-1 w-full" on:click={downloadDrawing}>
          <div class="text-blue-500 truncate">
            {drawing.name}
          </div>
          <div>
            <DownloadIcon />
          </div>
        </button>
        <button on:click={remove}>
          <XIcon />
        </button>
      </div>
    </div>
  {:else}
    <div class="flex items-center justify-between my-2">
      <div class="text-gray-400">Upload PDF or Image</div>
      <button on:click={upload}>
        <UploadIcon />
      </button>
    </div>
  {/if}
</div>

<Modal bind:this={dropzoneModal} closeable>
  <div slot="title">Upload Drawing</div>
  <div slot="content" class="space-y-2">
    {#if errorMsg}
      <ErrorMessage padding={false} message={errorMsg} on:close={() => (errorMsg = null)} />
    {/if}
    <p class="text-xs">
      Any of the following file types can be used to document this {recordName}'s shape: PDF, PNG, TIFF, GIF,
      or JPG.
    </p>
    <Dropzone
      on:drop={uploadFiles}
      bind:this={dropzone}
      accept="image/png, image/jpeg, image/tiff, image/gif, application/pdf" />
  </div>
</Modal>

<Modal
  bind:this={removeModal}
  on:confirm={removeDrawing}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Delete", type: "confirm", style: "danger" },
  ]}
  closeOnOutclick>
  <div slot="title">Delete Drawing</div>
  <div slot="content" class="space-y-2">
    <p>Are you sure you want to delete this drawing?</p>
    <p>
      {drawing?.name}
    </p>
  </div>
</Modal>
